'use client';

// Undo targeting adapted from OpenCode's
// packages/app/src/pages/session/use-session-commands.tsx (MIT, Copyright (c)
// 2025 opencode), commit c10134729dd2ce00beb18604ec91f10319f59a78. The Edit
// from here confirmation adapted from ChatView's pendingRevert and
// onRevertToTurnCount (apps/web/src/components/ChatView.tsx), pingdotgg/t3code
// (MIT, Copyright 2026 T3 Tools Inc.), commit 1de563c1491c7d82563e4553bf5bf689ce6adbb9.
import { c as _c } from "react/compiler-runtime";
import type { RewindDecision } from '@/lib/code-sessions/rewind-policy';
import { useIsMutating } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { SessionAction } from './session-action';
import type { SessionTurnState } from './session-turn-state';
import { loadComposerDraft, unloadComposerDraft } from './code-composer-draft';
import { blockedAction } from './session-action';
import { REWINDING_LABEL } from './session-turn-state';
import { messageEdits, undoTargetTurn } from './transcript-rewind';
import { useRewindMutations } from '@/data/code-sessions/mutations';
import { codeSessionKeys } from '@/data/code-sessions/queries';
import { isPersonTurn } from '@/data/code-sessions/turn-prompt';
import { CODE_SKILL_SELECTION_LIMIT } from '@/lib/code-sessions/skills';
import { deterministicPromptId } from '@/lib/code-sessions/turn-utils';
export const NOTHING_TO_UNDO = 'No message to undo';
export type SessionRewindCommands = {
  /** Edit from here on the last shown message: it asks first, as the bubble's button does. */
  undo: SessionAction;
  /** Bring back what the waiting edit hides, as the composer chip's Cancel does; undefined while none waits. */
  cancelEdit?: SessionAction;
};

/** T3's Edit from here confirmation for one message. */
export type EditConfirmation = {
  /** Confirming stops the current turn first. */
  stopsTurn: boolean;
  /** Rewind to before the message, reverting its files too or keeping them, then return its prompt to the composer. */
  confirm: (restoreFiles: boolean) => void;
  dismiss: () => void;
};
export type SessionRewind = {
  /** Undo and Cancel edit; the object keeps its identity while the transcript streams. */
  commands: SessionRewindCommands;
  /** A rewind request is in flight: the composer is inert and rewind actions wait. */
  busy: boolean;
  /** Edit from here on each prompt bubble, keyed by bubble ID. */
  edits: ReadonlyMap<string, SessionAction>;
  /** The confirmation Edit from here or Undo opened; null while none is open. */
  confirmation: EditConfirmation | null;
};
type RewindModel = {
  decisions: ReadonlyMap<string, RewindDecision>;
  edits: ReadonlyMap<string, SessionAction>;
  /** The last shown prompt OpenCode received. */
  undoTurnId: string | undefined;
};
const NO_REWIND: RewindModel = {
  decisions: new Map(),
  edits: new Map(),
  undoTurnId: undefined
};

/**
 * Edit from here, Undo, and Cancel edit for one session. Edit from here asks
 * first, as T3's does: it stages a rewind to before the message, reverting
 * files too or keeping them, and puts the prompt back in the composer at once.
 * The next prompt commits the rewind, and Cancel edit clears it. Undo is Edit
 * from here on the last shown message.
 */
export function useSessionRewind(input) {
  const $ = _c(66);
  const {
    sessionId,
    state
  } = input;
  const mutations = useRewindMutations(sessionId);
  let t0;
  if ($[0] !== sessionId) {
    t0 = codeSessionKeys.mutation(sessionId, "prompt");
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      mutationKey: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const sending = useIsMutating(t1) > 0;
  const [asking, setAsking] = useState(null);
  const busy = state?.rewinding ?? false;
  const editing = Boolean(state?.policy.rewindTurnId) && !sending;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ask: _temp,
      cancel: _temp2
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const latest = useRef(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = turnId => latest.current.ask(turnId);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const askLatest = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => latest.current.cancel();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const cancelLatest = t4;
  let t5;
  bb0: {
    if (state === null) {
      t5 = NO_REWIND;
      break bb0;
    }
    let t6;
    let t7;
    if ($[7] !== busy || $[8] !== state) {
      const decisions = messageEdits(state, busy ? REWINDING_LABEL : undefined);
      t6 = decisions;
      t7 = new Map([...decisions].map(t8 => {
        const [turnId_0, decision] = t8;
        const run = () => askLatest(turnId_0);
        return [deterministicPromptId(turnId_0), !decision.ok ? blockedAction(decision.reason) : decision.stopsTurn ? {
          stopsTurn: true,
          run
        } : {
          run
        }];
      }));
      $[7] = busy;
      $[8] = state;
      $[9] = t6;
      $[10] = t7;
    } else {
      t6 = $[9];
      t7 = $[10];
    }
    let t8;
    if ($[11] !== state.hidden || $[12] !== state.turns || $[13] !== state.unsent) {
      t8 = undoTargetTurn(state.turns.filter(isPersonTurn), state.hidden, state.unsent)?.id;
      $[11] = state.hidden;
      $[12] = state.turns;
      $[13] = state.unsent;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
      t9 = {
        decisions: t6,
        edits: t7,
        undoTurnId: t8
      };
      $[15] = t6;
      $[16] = t7;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    t5 = t9;
  }
  const model = t5;
  let t6;
  if ($[19] !== mutations || $[20] !== sessionId) {
    t6 = () => mutations.clear().then(() => unloadComposerDraft(sessionId));
    $[19] = mutations;
    $[20] = sessionId;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const clearEdit = t6;
  let t7;
  if ($[22] !== busy || $[23] !== clearEdit || $[24] !== mutations || $[25] !== sessionId || $[26] !== state?.drafts) {
    t7 = (turnId_1, restoreFiles) => {
      const draft = state?.drafts.get(turnId_1);
      if (draft === undefined || busy) {
        return;
      }
      let takeBack = _temp3;
      const staged = mutations.stage(turnId_1, {
        restoreFiles
      });
      staged.catch(() => takeBack());
      loadComposerDraft(sessionId, draft, {
        onLoaded: back => {
          takeBack = back;
        },
        onRefused: () => {
          staged.then(clearEdit).then(_temp4).catch(_temp5);
        }
      });
    };
    $[22] = busy;
    $[23] = clearEdit;
    $[24] = mutations;
    $[25] = sessionId;
    $[26] = state?.drafts;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const edit = t7;
  let t8;
  if ($[28] !== busy || $[29] !== edit || $[30] !== sessionId || $[31] !== state) {
    t8 = turnId_2 => {
      if (busy || !state?.drafts.has(turnId_2)) {
        return;
      }
      if (state.noFilesToRevert.has(turnId_2)) {
        edit(turnId_2, false);
        return;
      }
      setAsking({
        sessionId,
        turnId: turnId_2
      });
    };
    $[28] = busy;
    $[29] = edit;
    $[30] = sessionId;
    $[31] = state;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const ask = t8;
  let t9;
  if ($[33] !== busy || $[34] !== editing || $[35] !== mutations || $[36] !== sessionId) {
    t9 = () => {
      if (!editing || busy) {
        return;
      }
      let putBack = _temp6;
      unloadComposerDraft(sessionId, {
        onUnloaded: back_0 => {
          putBack = back_0;
        }
      });
      mutations.clear().catch(() => putBack());
    };
    $[33] = busy;
    $[34] = editing;
    $[35] = mutations;
    $[36] = sessionId;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  const cancel = t9;
  let t10;
  if ($[38] !== ask || $[39] !== cancel) {
    t10 = () => {
      latest.current = {
        ask,
        cancel
      };
    };
    $[38] = ask;
    $[39] = cancel;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  useLayoutEffect(t10);
  const askedTurnId = asking?.sessionId === sessionId ? asking.turnId : undefined;
  let t11;
  if ($[41] !== askedTurnId || $[42] !== model.decisions) {
    t11 = askedTurnId === undefined ? undefined : model.decisions.get(askedTurnId);
    $[41] = askedTurnId;
    $[42] = model.decisions;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const askedDecision = t11;
  let t12;
  if ($[44] !== askedDecision || $[45] !== askedTurnId || $[46] !== edit) {
    t12 = askedTurnId === undefined ? null : {
      stopsTurn: askedDecision?.ok === true && askedDecision.stopsTurn === true,
      confirm: restoreFiles_0 => {
        setAsking(null);
        edit(askedTurnId, restoreFiles_0);
      },
      dismiss: () => setAsking(null)
    };
    $[44] = askedDecision;
    $[45] = askedTurnId;
    $[46] = edit;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  const confirmation = t12;
  const undoTurnId = model.undoTurnId;
  let t13;
  if ($[48] !== model.decisions || $[49] !== undoTurnId) {
    t13 = undoTurnId === undefined ? undefined : model.decisions.get(undoTurnId);
    $[48] = model.decisions;
    $[49] = undoTurnId;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  const undoDecision = t13;
  const undoReason = undoDecision?.ok ? undefined : undoDecision?.reason ?? NOTHING_TO_UNDO;
  const undoStopsTurn = undoDecision?.ok ? undoDecision.stopsTurn : undefined;
  let t14;
  if ($[51] !== undoReason || $[52] !== undoStopsTurn || $[53] !== undoTurnId) {
    t14 = undoReason !== undefined || undoTurnId === undefined ? blockedAction(undoReason ?? NOTHING_TO_UNDO) : {
      stopsTurn: undoStopsTurn,
      run: () => askLatest(undoTurnId)
    };
    $[51] = undoReason;
    $[52] = undoStopsTurn;
    $[53] = undoTurnId;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  const undo = t14;
  let t15;
  if ($[55] !== busy || $[56] !== editing) {
    t15 = !editing ? undefined : busy ? blockedAction(REWINDING_LABEL) : {
      run: cancelLatest
    };
    $[55] = busy;
    $[56] = editing;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  const cancelEdit = t15;
  let t16;
  if ($[58] !== cancelEdit || $[59] !== undo) {
    t16 = {
      undo,
      cancelEdit
    };
    $[58] = cancelEdit;
    $[59] = undo;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  const commands = t16;
  let t17;
  if ($[61] !== busy || $[62] !== commands || $[63] !== confirmation || $[64] !== model.edits) {
    t17 = {
      commands,
      busy,
      edits: model.edits,
      confirmation
    };
    $[61] = busy;
    $[62] = commands;
    $[63] = confirmation;
    $[64] = model.edits;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  return t17;
}
function _temp6() {}
function _temp5() {}
function _temp4() {
  toast.error("Could not restore prompt", {
    description: `A prompt can have up to ${CODE_SKILL_SELECTION_LIMIT} skills. The edit was cancelled and your earlier messages were kept. Remove a skill and try again.`
  });
}
function _temp3() {}
function _temp2() {}
function _temp(_turnId) {}